/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import { observer } from 'mobx-react'
import React from 'react'
import { IDemographic, IDemographicIndividualResponse, IIndividual } from '../../../../services/CoverageDiscoveryResponseStateManager/contracts'
import { Container, Row, Col } from 'reactstrap'
import './EligibilityRequestDetails.scss'
import { CommonUtils } from '../../../../utils/CommonUtils'

interface IEligibilityRequestDetailsProps {
	transactionRequestDetails?: IIndividual
	demographicResponse?: IDemographicIndividualResponse
	title: string
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IEligibilityRequestDetailsState {}

@observer
export class EligibilityRequestDetailsComponent extends React.Component<IEligibilityRequestDetailsProps, IEligibilityRequestDetailsState> {
	render(): React.ReactNode {
		const title = this.props.title ? this.props.title : 'Request'
		const model = this.props.transactionRequestDetails
		const demographicResponse = this.props.demographicResponse
		const demographicModel = demographicResponse && demographicResponse.individual

		let zip
		if (model && demographicModel) {
			if (demographicModel.zip && demographicModel.zip.length > 5 && !demographicModel.zip.includes('-')) zip = demographicModel.zip.substring(0, 5) + '-' + demographicModel.zip.substring(5)
			else zip = demographicModel.zip
		}
		if (demographicModel == null) {
			return (
				model && (
					<Container className="patientdetails">
						<Row className="header">{title}</Row>
						<Row id="firstName">
							<Col xs="5">
								<strong>First Name:</strong>{' '}
							</Col>
							<Col xs="7">{model.firstName}</Col>
						</Row>
						<Row id="middleName">
							<Col xs="5">
								<strong>Middle Name: </strong>
							</Col>
							<Col xs="7">{model.middleName}</Col>
						</Row>
						<Row id="lastName">
							<Col xs="5">
								<strong>Last Name:</strong>{' '}
							</Col>
							<Col xs="7">{model.lastName}</Col>
						</Row>
						<Row id="gender">
							<Col xs="5">
								<strong>Gender:</strong>{' '}
							</Col>
							<Col xs="7">{model.gender}</Col>
						</Row>
						<Row id="dateOfBirth">
							<Col xs="5">
								<strong>DOB:</strong>{' '}
							</Col>
							<Col xs="7">{CommonUtils.toDisplayDate(model.dateOfBirth)}</Col>
						</Row>
						<Row id="ssn">
							<Col xs="5">
								<strong>SSN:</strong>{' '}
							</Col>
							<Col xs="7">{model.socialSecurityNumber}</Col>
						</Row>
						<Row id="addressLine1">
							<Col xs="5">
								<strong>Address Line 1:</strong>{' '}
							</Col>
							<Col xs="7">{model.address1}</Col>
						</Row>
						<Row id="addressLine2">
							<Col xs="5">
								<strong>Address Line 2:</strong>{' '}
							</Col>
							<Col xs="7">{model.address2}</Col>
						</Row>
						<Row id="city">
							<Col xs="5">
								<strong>City:</strong>{' '}
							</Col>
							<Col xs="7">{model.city}</Col>
						</Row>
						<Row id="state">
							<Col xs="5">
								<strong>State:</strong>{' '}
							</Col>
							<Col xs="7">{typeof model.stateCode !== 'undefined' && model.stateCode !== null && model.stateCode.name}</Col>
						</Row>
						<Row id="zipCode">
							<Col xs="5">
								<strong>Zip Code:</strong>{' '}
							</Col>
							<Col xs="7">{model.zip}</Col>
						</Row>
						<Row id="phoneNumber">
							<Col xs="5">
								<strong>Phone Number:</strong>{' '}
							</Col>
							<Col xs="7">{model.phoneNumber && model.phoneNumber.length == 10 ? CommonUtils.toDisplayPhoneNumber(model.phoneNumber) : model.phoneNumber}</Col>
						</Row>
					</Container>
				)
			)
		} else {
			return (
				model && (
					<Container className="patientdetails">
						<Row className="header">{title}</Row>
						<Row id="firstName">
							<Col xs="4">
								<strong>First Name:</strong>{' '}
							</Col>
							<Col xs="3">
								<div style={{ display: model.firstName ? '' : 'none' }}>{model.firstName}</div>
							</Col>
							<Col xs="3" className={CommonUtils.isDifferent(model.firstName, demographicModel.firstName) ? 'showDemographicData' : 'hideDemographicData'}>
								{demographicModel.firstName}
							</Col>
						</Row>
						<Row id="middleName">
							<Col xs="4">
								<strong>Middle Name: </strong>
							</Col>
							<Col xs="3">
								<div style={{ display: model.middleName ? '' : 'none' }}>{model.middleName}</div>
							</Col>
							<Col xs="3" className={CommonUtils.isDifferent(model.middleName, demographicModel.middleName) ? 'showDemographicData' : 'hideDemographicData'}>
								{demographicModel.middleName}
							</Col>
						</Row>
						<Row id="lastName">
							<Col xs="4">
								<strong>Last Name:</strong>{' '}
							</Col>
							<Col xs="3">
								<div style={{ display: model.lastName ? '' : 'none' }}>{model.lastName}</div>
							</Col>
							<Col xs="3" className={CommonUtils.isDifferent(model.lastName, demographicModel.lastName) ? 'showDemographicData' : 'hideDemographicData'}>
								{demographicModel.lastName}
							</Col>
						</Row>
						<Row id="gender">
							<Col xs="4">
								<strong>Gender:</strong>{' '}
							</Col>
							<Col xs="3">
								<div style={{ display: model.gender ? '' : 'none' }}>{model.gender}</div>
							</Col>
							<Col xs="3" className={CommonUtils.isDifferent(model.gender, demographicModel.gender) ? 'showDemographicData' : 'hideDemographicData'}>
								{demographicModel.gender}
							</Col>
						</Row>
						<Row id="dateOfBirth">
							<Col xs="4">
								<strong>DOB:</strong>{' '}
							</Col>
							<Col xs="3">
								<div style={{ display: model.dateOfBirth ? '' : 'none' }}>{CommonUtils.toDisplayDate(model.dateOfBirth)}</div>
							</Col>
							<Col xs="3" className={CommonUtils.datesAreEqual(model.dateOfBirth, demographicModel.dateOfBirth) ? 'hideDemographicData' : 'showDemographicData'}>
								{CommonUtils.toDisplayDate(demographicModel.dateOfBirth)}
							</Col>
						</Row>
						<Row id="ssn">
							<Col xs="4">
								<strong>SSN:</strong>{' '}
							</Col>
							<Col xs="3">
								<div style={{ display: model.socialSecurityNumber ? '' : 'none' }}>{model.socialSecurityNumber}</div>
							</Col>
							<Col xs="3" className={CommonUtils.isDifferent(model.socialSecurityNumber, demographicModel.socialSecurityNumber) ? 'showDemographicData' : 'hideDemographicData'}>
								{demographicModel.socialSecurityNumber}
							</Col>
						</Row>
						<Row id="addressLine1">
							<Col xs="4">
								<strong>Address Line 1:</strong>{' '}
							</Col>
							<Col xs="3">
								<div style={{ display: model.address1 ? '' : 'none' }}>{model.address1}</div>
							</Col>
							<Col xs="3" className={CommonUtils.isDVAddressDifferent(model.address1, model.address2, demographicModel.address1) ? 'showDemographicData' : 'hideDemographicData'}>
								{demographicModel.address1}
							</Col>
						</Row>
						<Row id="addressLine2">
							<Col xs="4">
								<strong>Address Line 2:</strong>{' '}
							</Col>
							<Col xs="6">{model.address2}</Col>
						</Row>
						<Row id="city">
							<Col xs="4">
								<strong>City:</strong>{' '}
							</Col>
							<Col xs="3">
								<div style={{ display: model.city ? '' : 'none' }}>{model.city}</div>
							</Col>
							<Col xs="3" className={CommonUtils.isDifferent(model.city, demographicModel.city) ? 'showDemographicData' : 'hideDemographicData'}>
								{demographicModel.city}
							</Col>
						</Row>
						<Row id="state">
							<Col xs="4">
								<strong>State:</strong>{' '}
							</Col>
							<Col xs="3">
								<div style={{ display: model.stateCode ? '' : 'none' }}>{typeof model.stateCode !== 'undefined' && model.stateCode !== null && model.stateCode.name}</div>
							</Col>
							<Col xs="3" className={CommonUtils.isDifferent(model.stateCodeId, demographicModel.stateCodeId) ? 'showDemographicData' : 'hideDemographicData'}>
								{typeof demographicModel.stateCode !== 'undefined' && demographicModel.stateCode !== null && demographicModel.stateCode.name}
							</Col>
						</Row>
						<Row id="zipCode">
							<Col xs="4">
								<strong>Zip Code:</strong>{' '}
							</Col>
							<Col xs="3">
								<div style={{ display: model.zip ? '' : 'none' }}>{model.zip}</div>
							</Col>
							<Col xs="3" className={CommonUtils.isZipCodeDifferent(model.zip, demographicModel.zip) ? 'showDemographicData' : 'hideDemographicData'}>
								{zip}
							</Col>
						</Row>
						<Row id="phoneNumber">
							<Col xs="4">
								<strong>Phone Number:</strong>{' '}
							</Col>
							<Col xs="3">
								<div style={{ display: model.phoneNumber ? '' : 'none' }}>{model.phoneNumber && model.phoneNumber.length == 10 ? CommonUtils.toDisplayPhoneNumber(model.phoneNumber) : model.phoneNumber}</div>
							</Col>
							<Col xs="3" className={CommonUtils.isDifferent(model.phoneNumber, demographicModel.phoneNumber) ? 'showDemographicData' : 'hideDemographicData'}>
								{demographicModel.phoneNumber && demographicModel.phoneNumber.length == 10 ? CommonUtils.toDisplayPhoneNumber(demographicModel.phoneNumber) : demographicModel.phoneNumber}
							</Col>
						</Row>
					</Container>
				)
			)
		}
	}
}
